import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { portalApi, setPortalSession, getPortalToken, formatPortalError } from "@/api/portalClient";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { LogIn, Loader2, ShieldCheck, KeyRound } from "lucide-react";

export default function EmployeePortalLogin() {
  const navigate = useNavigate();
  const { refresh: refreshAuth } = useAuth() || {};
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (getPortalToken()) return <Navigate to="/employee-portal" replace />;

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !password) return toast.error("Syötä sähköposti ja salasana");
    setLoading(true);
    try {
      const r = await portalApi.post("/employee-portal/login", { email, password });
      setPortalSession(r.data.token, r.data.employee);
      // Phase 17C: staff_user saa myös main-app cookies → aseta session flag ja
      // refresh AuthContext, jotta klikatessa moduuli-syvälinkkiä (esim. /restaurant/waiter)
      // ProtectedRoute saa heti voimassa olevan user-tilan.
      if (r.data.employee?.subject_type === "staff_user") {
        try {
          sessionStorage.setItem("apps2go.session_active", "1");
          if (r.data.employee?.email) sessionStorage.setItem("apps2go.session_user_email", r.data.employee.email);
        } catch { /* ignore */ }
        try { await refreshAuth?.(); } catch { /* ignore */ }
      }
      toast.success(`Tervetuloa, ${r.data.employee.first_name || ""}!`);
      if (r.data.employee?.must_change_password) {
        navigate("/employee-portal/change-password");
      } else {
        navigate("/employee-portal");
      }
    } catch (err) {
      toast.error(formatPortalError(err));
    }
    setLoading(false);
  };

  return (
    <div className="app-bg flex items-center justify-center px-4 py-12 min-h-screen" data-testid="emp-portal-login">
      <div className="w-full max-w-md">
        <div className="glass-card p-8 space-y-6">
          <div className="text-center relative z-10">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-amber-500/30">
              <ShieldCheck className="w-7 h-7 text-white" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Apps2Go Hub</p>
            <h1 className="text-2xl font-display font-bold mt-1 gradient-text-luxe">Työntekijän portaali</h1>
            <p className="text-xs text-white/50 mt-2">Kirjaudu sisään nähdäksesi omat työvuorosi ja palkkalaskelmasi</p>
          </div>

          <form onSubmit={submit} className="space-y-4 relative z-10">
            <div>
              <Label className="text-xs text-white/70">Sähköposti</Label>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="etunimi.sukunimi@..."
                className="mt-1 bg-black/30 border-white/10"
                autoComplete="email"
                data-testid="emp-portal-email"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs text-white/70">Salasana</Label>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="•••••••"
                className="mt-1 bg-black/30 border-white/10"
                autoComplete="current-password"
                data-testid="emp-portal-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="btn-luxe w-full inline-flex items-center justify-center gap-2"
              data-testid="emp-portal-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              Kirjaudu sisään
            </Button>
          </form>

          <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-100 leading-relaxed relative z-10">
            <p className="flex items-center gap-1 font-semibold mb-1"><KeyRound className="w-3.5 h-3.5" /> Eikö sinulla ole kirjautumistietoja?</p>
            <p>Ota yhteyttä työnantajaasi (esimies tai henkilöstöhallinto) – he asettavat sinulle pääsyn ja antavat tilapäisen salasanan.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
